import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, FolderPlus, Star, RotateCcw, Trash2, Image as ImageIcon, ChevronRight, Plus } from "lucide-react";
import { colors, type, spacing, radius, formatCents } from "@/theme";
import { api, mediaUrl } from "@/lib/api";
import { useRequireAuth } from "@/lib/useRequireAuth";

export default function AdminMenu() {
  const navigate = useNavigate();
  const { user } = useRequireAuth();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newCat, setNewCat] = useState("");

  const load = useCallback(async () => { const d = await api.adminMenuFull(); setCats(d.categorie); setLoading(false); }, []);
  useEffect(() => { if (user) load(); }, [user, load]);
  if (!user) return null;

  const addCat = async () => { if (!newCat.trim()) return; await api.createCategory({ nome: newCat.trim() }); setNewCat(""); setAdding(false); load(); };
  const delCat = async (id) => { await api.deleteCategory(id); load(); };
  const markOggi = async (c) => { await api.updateCategory(c.id, { speciale: c.speciale === "oggi" ? null : "oggi" }); load(); };
  const azzera = async (id) => { await api.azzeraOggi(id); load(); };

  return (
    <div style={{ minHeight: "100vh", background: colors.bg }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${spacing.md}px ${spacing.lg}px` }}>
          <button data-testid="back-button" onClick={() => navigate(-1)} style={plain}><ChevronLeft size={26} color={colors.ink} /></button>
          <div style={{ ...type.serif, fontSize: 22, color: colors.ink }}>Menu</div>
          <button data-testid="add-cat-toggle" onClick={() => setAdding((v) => !v)} style={plain}><FolderPlus size={22} color={colors.accent} /></button>
        </div>

        {adding ? (
          <div style={{ display: "flex", gap: spacing.sm, padding: `0 ${spacing.lg}px`, marginBottom: spacing.sm }}>
            <input data-testid="new-cat-input" style={{ ...input, flex: 1 }} value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Nome categoria" autoFocus onKeyDown={(e) => e.key === "Enter" && addCat()} />
            <button data-testid="add-cat-button" onClick={addCat} style={{ background: colors.accent, borderRadius: radius.md, padding: `0 ${spacing.lg}px`, border: "none", color: colors.onAccent, ...type.sansSemi, fontSize: 15 }}>Crea</button>
          </div>
        ) : null}

        {loading ? <div style={{ padding: spacing.xl, textAlign: "center", ...type.sans, color: colors.accent }}>Caricamento…</div> : (
          <div style={{ padding: spacing.lg, display: "flex", flexDirection: "column", gap: spacing.lg }}>
            {cats.map((c) => {
              const withPhoto = c.items.filter((it) => it.foto_url || it.foto_thumb_url).length;
              const partial = withPhoto > 0 && withPhoto < c.items.length;
              return (
                <div key={c.id} style={{ display: "flex", flexDirection: "column", gap: spacing.sm }}>
                  <div style={{ display: "flex", alignItems: "center", gap: spacing.md }}>
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: spacing.sm }}>
                      <span style={{ ...type.sansBold, fontSize: 20, color: colors.ink }}>{c.nome}</span>
                      {c.speciale === "oggi" ? <span style={{ background: colors.accentSoft, borderRadius: radius.sm, padding: "2px 8px", ...type.sansSemi, fontSize: 10, color: colors.accent, letterSpacing: 1 }}>OGGI</span> : null}
                    </div>
                    <button data-testid={`cat-oggi-${c.id}`} onClick={() => markOggi(c)} style={plain}><Star size={18} color={c.speciale === "oggi" ? colors.warning : colors.inkSoft} /></button>
                    {c.speciale === "oggi" ? <button data-testid={`cat-azzera-${c.id}`} onClick={() => azzera(c.id)} style={plain}><RotateCcw size={18} color={colors.inkSoft} /></button> : null}
                    <button data-testid={`cat-del-${c.id}`} onClick={() => delCat(c.id)} style={plain}><Trash2 size={18} color={colors.danger} /></button>
                  </div>

                  {partial ? (
                    <div data-testid={`photo-warn-${c.id}`} style={{ background: colors.paperDeep, borderLeft: `2px solid ${colors.warning}`, borderRadius: radius.sm, padding: `${spacing.sm}px ${spacing.md}px` }}>
                      <span style={{ ...type.sansMedium, fontSize: 12.5, color: colors.warning, lineHeight: "18px" }}>In {c.nome} {withPhoto} piatti su {c.items.length} hanno la foto. Meglio completarle o toglierle tutte.</span>
                    </div>
                  ) : null}

                  {c.items.map((it) => (
                    <button key={it.id} data-testid={`admin-item-${it.id}`} onClick={() => navigate(`/admin/item?id=${it.id}`)} style={{ display: "flex", alignItems: "center", gap: spacing.md, background: colors.surface, borderRadius: radius.md, padding: spacing.sm, border: `1px solid ${colors.line}`, width: "100%", textAlign: "left" }}>
                      {it.foto_thumb_url || it.foto_url ? <img src={mediaUrl(it.foto_thumb_url || it.foto_url)} alt={it.nome} style={{ width: 48, height: 48, borderRadius: radius.sm, objectFit: "cover" }} /> : <div style={{ width: 48, height: 48, borderRadius: radius.sm, background: colors.surfaceTertiary, display: "flex", alignItems: "center", justifyContent: "center" }}><ImageIcon size={18} color={colors.lineStrong} /></div>}
                      <div style={{ flex: 1 }}>
                        <div style={{ ...type.sansSemi, fontSize: 15, color: it.disponibile ? colors.ink : colors.inkSoft }}>{it.nome}</div>
                        <div style={{ ...type.sans, fontSize: 13, color: colors.inkSoft, marginTop: 2 }}>{formatCents(it.prezzo_cent)}{it.prezzi_fascia?.length ? " · cena diverso" : ""}</div>
                      </div>
                      {!it.disponibile ? <span style={{ background: colors.dangerSoft, borderRadius: radius.sm, padding: "2px 8px", ...type.sansSemi, fontSize: 11, color: colors.danger }}>Esaurito</span> : null}
                      <ChevronRight size={20} color={colors.inkSoft} />
                    </button>
                  ))}

                  <button data-testid={`add-item-${c.id}`} onClick={() => navigate(`/admin/item?category_id=${c.id}`)} style={{ display: "flex", alignItems: "center", gap: spacing.xs, alignSelf: "flex-start", background: "none", border: "none", padding: "4px 0" }}>
                    <Plus size={16} color={colors.accent} /><span style={{ ...type.sansSemi, fontSize: 14, color: colors.accent }}>Aggiungi piatto</span>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const plain = { background: "none", border: "none", display: "flex", padding: 0 };
const input = { background: colors.surface, border: `1px solid ${colors.line}`, borderRadius: radius.md, height: 48, padding: `0 ${spacing.md}px`, ...type.sans, fontSize: 15, color: colors.ink };
